import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { CalendarClock, Camera, ImagePlus } from "lucide-react";
import { formatDate, initials } from "@/lib/tp";
import { fileToResizedDataUrl } from "@/lib/image";

const COLORS = ["#0A84FF", "#FF375F", "#30D158", "#FF9F0A", "#BF5AF2", "#5AC8FA", "#FFD60A", "#FF6482"];

function toDateFromISO(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toISOFromDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function ClientModal({ open, onOpenChange }) {
  const { addClient } = useData();
  const inputRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    company: "",
    contact_email: "",
    color: COLORS[0],
    contract_expiry: null,
    avatar_url: null,
  });
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Immagine troppo grande (max 4MB)");
    try {
      const url = await fileToResizedDataUrl(file, 320, 0.85);
      setForm((f) => ({ ...f, avatar_url: url }));
    } catch {
      toast.error("Errore caricamento immagine");
    }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Nome cliente richiesto");
    setSaving(true);
    try {
      await addClient({
        name: form.name,
        company: form.company || null,
        contact_email: form.contact_email || null,
        color: form.color,
        contract_expiry: form.contract_expiry,
        avatar_url: form.avatar_url,
      });
      toast.success("Cliente aggiunto");
      onOpenChange(false);
      setForm({ name: "", company: "", contact_email: "", color: COLORS[0], contract_expiry: null, avatar_url: null });
    } catch (e) {
      toast.error("Errore durante la creazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Nuovo cliente</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="client-avatar-picker"
              className="relative h-16 w-16 rounded-2xl overflow-hidden shrink-0 shadow-lg group focus:outline-none focus:ring-2 focus:ring-fuchsia-500/60"
              style={{
                backgroundColor: form.avatar_url ? "transparent" : form.color,
                backgroundImage: form.avatar_url ? `url(${form.avatar_url})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {!form.avatar_url && (
                <span className="flex items-center justify-center h-full w-full text-white font-bold">
                  {initials(form.name || "?")}
                </span>
              )}
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-colors opacity-0 group-hover:opacity-100">
                <Camera size={18} className="text-white" />
              </span>
            </button>
            <div className="flex-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => inputRef.current?.click()}
                className="text-sm text-zinc-300 hover:text-white hover:bg-white/10"
              >
                <ImagePlus size={14} className="mr-1.5" />
                {form.avatar_url ? "Cambia immagine" : "Carica immagine"}
              </Button>
              {form.avatar_url && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setForm({ ...form, avatar_url: null })}
                  className="text-xs text-zinc-500 hover:text-red-400"
                >
                  Rimuovi
                </Button>
              )}
            </div>
            <input
              ref={inputRef}
              data-testid="client-avatar-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFile}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Nome cliente</Label>
            <Input
              data-testid="client-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white h-11"
              placeholder="Es. Acme Corp"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Azienda / tag</Label>
            <Input
              data-testid="client-company-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="bg-white/5 border-white/10 text-white h-11"
              placeholder="Opzionale"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Email di contatto</Label>
            <Input
              data-testid="client-email-input"
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="bg-white/5 border-white/10 text-white h-11"
              placeholder="Opzionale"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Scadenza contratto
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="client-expiry-button"
                  variant="outline"
                  className="w-full h-11 justify-start bg-white/5 border-white/10 text-white hover:bg-white/10"
                >
                  <CalendarClock size={14} className="mr-2 text-zinc-400" />
                  {form.contract_expiry ? formatDate(form.contract_expiry) : "Nessuna scadenza"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="tp-glass-strong border-white/10 p-0 text-white w-auto">
                <Calendar
                  mode="single"
                  selected={toDateFromISO(form.contract_expiry)}
                  onSelect={(d) => setForm({ ...form, contract_expiry: toISOFromDate(d) })}
                  initialFocus
                />
                {form.contract_expiry && (
                  <div className="p-2 border-t border-white/10">
                    <button
                      onClick={() => setForm({ ...form, contract_expiry: null })}
                      className="w-full text-xs text-zinc-400 hover:text-white py-1.5"
                    >
                      Rimuovi scadenza
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Colore</Label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setForm({ ...form, color: c })}
                  className={`h-8 w-8 rounded-full border-2 transition-transform ${
                    form.color === c ? "border-white scale-110" : "border-transparent"
                  }`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <Button
            onClick={submit}
            data-testid="client-save-button"
            disabled={saving}
            className="tp-brand-btn w-full rounded-xl"
          >
            {saving ? "Salvo..." : "Crea cliente"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
